/**
 * OPC package reading and **part-preserving** repacking.
 *
 * DOCX, XLSX, PPTX, HWPX, EPUB and ODF are all ZIP containers of XML parts.
 * This module is the shared floor under all of them, and its whole reason to
 * exist is the save path.
 *
 * The usual way to save an edited Office file is to parse it into a model and
 * serialise the model back out. Everything the model does not understand is
 * lost — macros, charts, pivot caches, SmartArt, tracked changes, embedded
 * fonts. §13 and §24 forbid exactly that: "Do not silently remove unsupported
 * document features during saving."
 *
 * So {@link repackPackage} rewrites only the parts that were edited and copies
 * every other entry's **compressed bytes verbatim**. Untouched parts come out
 * bit-identical, which makes preservation a property of the algorithm rather
 * than a promise about how thorough the parser is.
 */
import { deflateSync, inflateSync } from 'fflate';
import {
  DEFAULT_ZIP_LIMITS, readRawEntry, readZipDirectory,
} from '@moduviewer/core';
import type { ZipDirectory, ZipEntry, ZipLimits } from '@moduviewer/core';
import { HandlerError } from '../contract.js';

export interface OpcPackage {
  readonly bytes: Uint8Array;
  readonly directory: ZipDirectory;
  readonly entryByName: ReadonlyMap<string, ZipEntry>;
}

const METHOD_STORE = 0;
const METHOD_DEFLATE = 8;

export function openPackage(bytes: Uint8Array, limits: ZipLimits = DEFAULT_ZIP_LIMITS): OpcPackage {
  const directory = readZipDirectory(bytes, limits);

  // §21 violations are refusals, not warnings: a package with a path-traversal
  // entry is not a document we are willing to open at all.
  const blocking = directory.violations.filter((v) => v.kind === 'unsafe-path' || v.kind === 'zip64-unsupported');
  if (blocking.length > 0) {
    throw new HandlerError(
      'error.unableToRead',
      `package rejected: ${blocking.map((v) => v.kind).join(', ')}`,
    );
  }

  return {
    bytes,
    directory,
    entryByName: new Map(directory.entries.map((e) => [e.name, e])),
  };
}

export function hasPart(pkg: OpcPackage, name: string): boolean {
  return pkg.entryByName.has(name);
}

/** Decompressed bytes of one part. */
export function readPart(pkg: OpcPackage, name: string): Uint8Array {
  const entry = pkg.entryByName.get(name);
  if (!entry) throw new HandlerError('error.damagedOrIncomplete', `missing part: ${name}`);
  if (entry.encrypted) throw new HandlerError('error.passwordProtected', `encrypted part: ${name}`);

  const raw = readRawEntry(pkg.bytes, entry);
  if (!raw) throw new HandlerError('error.damagedOrIncomplete', `unreadable part: ${name}`);

  if (entry.method === METHOD_STORE) return raw;
  if (entry.method === METHOD_DEFLATE) {
    try {
      return inflateSync(raw, { out: new Uint8Array(entry.uncompressedSize) });
    } catch (err) {
      throw new HandlerError('error.damagedOrIncomplete', `could not inflate ${name}: ${(err as Error).message}`);
    }
  }
  throw new HandlerError('error.featuresUnsupported', `unsupported compression method ${entry.method} for ${name}`);
}

export function readPartText(pkg: OpcPackage, name: string): string {
  // OOXML parts are UTF-8 by specification; there is no encoding to detect.
  return new TextDecoder('utf-8').decode(readPart(pkg, name));
}

/** Part names matching a prefix, in package order. */
export function partsWithPrefix(pkg: OpcPackage, prefix: string): string[] {
  return pkg.directory.entries.map((e) => e.name).filter((n) => n.startsWith(prefix));
}

/* ------------------------------------------------------------------ *
 * repacking
 * ------------------------------------------------------------------ */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface PendingEntry {
  readonly name: string;
  readonly data: Uint8Array;       // bytes as they will be stored
  readonly method: number;
  readonly crc: number;
  readonly uncompressedSize: number;
  readonly flags: number;
}

export interface RepackResult {
  readonly bytes: Uint8Array;
  /** Parts rewritten by this save. */
  readonly replaced: readonly string[];
  /** Parts copied without recompression — preserved bit-for-bit. */
  readonly preserved: readonly string[];
}

/**
 * Rebuild the package, substituting the given parts.
 *
 * @param replacements Part name → new decompressed bytes. A part not listed
 *   here is copied in its original compressed form.
 * @param added Parts that do not exist in the original and should be appended.
 */
export function repackPackage(
  pkg: OpcPackage,
  replacements: ReadonlyMap<string, Uint8Array>,
  added: ReadonlyMap<string, Uint8Array> = new Map(),
): RepackResult {
  const pending: PendingEntry[] = [];
  const replaced: string[] = [];
  const preserved: string[] = [];

  for (const entry of pkg.directory.entries) {
    const replacement = replacements.get(entry.name);
    if (replacement) {
      pending.push(makeEntry(entry.name, replacement, entry.method === METHOD_STORE));
      replaced.push(entry.name);
      continue;
    }

    const raw = readRawEntry(pkg.bytes, entry);
    if (!raw) {
      throw new HandlerError('error.unableToRead', `could not read original data for ${entry.name}`);
    }
    // Copy the stored bytes and the metadata that describes them. Nothing is
    // decompressed, so nothing can be subtly changed.
    pending.push({
      name: entry.name,
      data: raw,
      method: entry.method,
      // The central directory already holds a CRC computed over the original
      // uncompressed data; reusing it avoids inflating just to recompute it.
      crc: readCrcFromCentralDirectory(pkg, entry),
      uncompressedSize: entry.uncompressedSize,
      flags: 0x0800,
    });
    preserved.push(entry.name);
  }

  for (const [name, data] of added) {
    if (pkg.entryByName.has(name)) continue; // already handled as a replacement
    pending.push(makeEntry(name, data, false));
    replaced.push(name);
  }

  return { bytes: writeZip(pending), replaced, preserved };
}

/**
 * `mimetype` in ODF, EPUB and HWPX must stay first and uncompressed, so a
 * replacement for it is stored rather than deflated.
 */
function makeEntry(name: string, data: Uint8Array, store: boolean): PendingEntry {
  const crc = crc32(data);
  if (store || name === 'mimetype') {
    return { name, data, method: METHOD_STORE, crc, uncompressedSize: data.length, flags: 0x0800 };
  }
  const compressed = deflateSync(data, { level: 6 });
  // Deflate can enlarge already-random or tiny data; storing is then both
  // smaller and faster to read back.
  if (compressed.length >= data.length) {
    return { name, data, method: METHOD_STORE, crc, uncompressedSize: data.length, flags: 0x0800 };
  }
  return { name, data: compressed, method: METHOD_DEFLATE, crc, uncompressedSize: data.length, flags: 0x0800 };
}

function readCrcFromCentralDirectory(pkg: OpcPackage, entry: ZipEntry): number {
  // Walk to this entry's central-directory record to lift its stored CRC.
  const view = new DataView(pkg.bytes.buffer, pkg.bytes.byteOffset, pkg.bytes.byteLength);
  let pos = findCentralDirectoryStart(pkg, view);
  for (const candidate of pkg.directory.entries) {
    const nameLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    if (candidate.name === entry.name) return view.getUint32(pos + 16, true);
    pos += 46 + nameLen + extraLen + commentLen;
  }
  // Unreachable for a directory we just parsed; recompute rather than throw.
  return crc32(readPart(pkg, entry.name));
}

function findCentralDirectoryStart(pkg: OpcPackage, view: DataView): number {
  for (let i = pkg.bytes.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) return view.getUint32(i + 16, true);
  }
  throw new HandlerError('error.damagedOrIncomplete', 'end-of-central-directory record not found');
}

function writeZip(entries: readonly PendingEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const e of entries) {
    const nameBytes = encoder.encode(e.name);

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, e.flags, true);
    lv.setUint16(8, e.method, true);
    lv.setUint16(10, 0, true);            // mod time
    lv.setUint16(12, 0x21, true);         // mod date — fixed, so saves are reproducible
    lv.setUint32(14, e.crc, true);
    lv.setUint32(18, e.data.length, true);
    lv.setUint32(22, e.uncompressedSize, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    locals.push(local, e.data);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, e.flags, true);
    cv.setUint16(10, e.method, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x21, true);
    cv.setUint32(16, e.crc, true);
    cv.setUint32(20, e.data.length, true);
    cv.setUint32(24, e.uncompressedSize, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centrals.push(central);

    offset += local.length + e.data.length;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const total = offset + centralSize + eocd.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const chunk of locals) { out.set(chunk, p); p += chunk.length; }
  for (const chunk of centrals) { out.set(chunk, p); p += chunk.length; }
  out.set(eocd, p);
  return out;
}
